import { useMemo, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { IGetSuperuserWorkspaceSwitcherResponseDto } from '@site-mate/sitemate-global-shared';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import { getSuperuserWorkspaceSearchQueryOptions } from '@/queries/workspace-switcher';

import SearchBar from '../common/search-bar';
import WarningIcon from '../icons/warning';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import CreateWorkspace from './create-workspace';
import { generateWorkspaceUrl, getAvatarColor, getInitial, PRODUCT_ICONS } from './workspace-switcher-menu.utils';

const MIN_SEARCH_LENGTH = 2;

function NameWithAvatar({ name }: { name: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div
        className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold text-white"
        style={{ backgroundColor: getAvatarColor(name), borderRadius: '2px' }}>
        {getInitial(name)}
      </div>
      <span className="truncate">{name}</span>
    </div>
  );
}

function SearchResultsTable({ workspaces }: { workspaces: IGetSuperuserWorkspaceSwitcherResponseDto[] }) {
  return (
    <table className="w-full text-left text-sm">
      <thead className="sticky top-0 z-10">
        <tr className="border-grey-3 bg-grey-1 text-default-text border-b text-sm font-semibold">
          <th className="px-3 py-2 text-left whitespace-nowrap">Sitemate Start Account Name</th>
          <th className="px-3 py-2 text-left whitespace-nowrap">Sitemate Start Account ID</th>
          <th className="px-3 py-2 text-left whitespace-nowrap">Workspace Name</th>
          <th className="px-3 py-2 text-left whitespace-nowrap">Workspace Type</th>
          <th className="px-3 py-2 text-left whitespace-nowrap">Workspace ID</th>
        </tr>
      </thead>
      <tbody>
        {workspaces.map((workspace) => {
          const icon = PRODUCT_ICONS[workspace.product];
          const url = generateWorkspaceUrl({
            id: workspace.workspaceId,
            product: workspace.product,
            legacyId: workspace.legacyId,
            region: workspace.region,
            subdomain: workspace.subdomain,
          });
          return (
            <tr
              key={workspace.workspaceId}
              className="border-grey-3 hover:bg-grey-1 cursor-pointer border-b last:border-0"
              onClick={() => {
                if (url) window.location.href = url;
              }}>
              <td className="max-w-64 px-3 py-2">
                <div className="truncate" title={workspace.accountName}>
                  <NameWithAvatar name={workspace.accountName} />
                </div>
              </td>
              <td className="text-secondary-text px-3 py-2 whitespace-nowrap">{workspace.accountId}</td>
              <td className="max-w-64 px-3 py-2">
                <div className="truncate" title={workspace.name}>
                  <NameWithAvatar name={workspace.name} />
                </div>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <div className="flex items-center gap-1">
                  {icon && <img src={icon} alt={workspace.product} className="h-4 w-4" />}
                  <span>{workspace.product}</span>
                </div>
              </td>
              <td className="text-secondary-text px-3 py-2 whitespace-nowrap">{workspace.workspaceId}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface WorkspaceSwitcherTableProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WorkspaceSwitcherTable({ isOpen, onClose }: WorkspaceSwitcherTableProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  const shouldSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;

  const {
    data: searchResults,
    isLoading,
    error,
  } = useQuery({
    ...getSuperuserWorkspaceSearchQueryOptions({ search: debouncedSearch }),
    enabled: shouldSearch,
  });

  const content = useMemo(() => {
    if (!shouldSearch) {
      return (
        <div className="text-secondary-text flex min-h-20 items-center justify-center text-sm">
          <span>Start searching for a workspace or account</span>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex min-h-20 items-center justify-center">
          <GearbeltLoader size={27} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex min-h-20 flex-col items-center justify-center gap-1 py-4">
          <WarningIcon size={36} className="text-[#F99226]" />
          <span className="text-secondary-text text-sm">Unable to load workspaces. Please try again.</span>
        </div>
      );
    }

    if (!searchResults?.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-5">
          <img src={manholeShrugVector} alt="No results" className="h-28" />
          <span className="text-secondary-text mt-4 text-sm">Oops! No results</span>
        </div>
      );
    }

    return <SearchResultsTable workspaces={searchResults} />;
  }, [shouldSearch, isLoading, error, searchResults]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          className="flex w-[calc(100vw-60px)] flex-col gap-0 overflow-hidden border-white text-sm md:w-[80vw] md:max-w-[1518px]"
          style={{ maxHeight: 'calc(100vh - 128px)' }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setSearch('');
            inputRef.current?.focus();
          }}>
          <DialogHeader className="shrink-0 pb-2.5">
            <DialogTitle>Search Workspaces & Sitemate Start Accounts</DialogTitle>
          </DialogHeader>

          <div className="border-grey-3 bg-grey-2 shrink-0 rounded-t border p-1">
            <SearchBar ref={inputRef} search={search} setSearch={setSearch} placeholder="Search by Account ID, Workspace Name or ID..." className="w-[362px]" />
          </div>

          <div className="border-grey-3 min-h-0 overflow-auto border border-t-0">{content}</div>

          <div className="shrink-0 pt-4">
            <Button
              variant="success"
              className="w-fit p-2 font-medium"
              onClick={() => {
                onClose();
                setIsCreateModalOpen(true);
              }}>
              Create Sitemate Start Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateModalOpen} onOpenChange={(open) => !open && setIsCreateModalOpen(false)}>
        <DialogContent className="w-[500px] border-white">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
          </DialogHeader>
          <CreateWorkspace account={null} />
        </DialogContent>
      </Dialog>
    </>
  );
}
